import { buildApiUrl } from '../utils/apiConfig';
import type { PageResponse } from './polarisService';
import { apiClient } from "../utils/apiClient";

export type SupportTicketStatus = 'RAISED' | 'IN_PROGRESS' | 'CLOSED';
export type SupportTicketSeverity = 'HIGH' | 'MEDIUM' | 'LOW';

export interface SupportTicketSummary {
    id: number;
    ticketNumber: string;
    createdAt: string;
    title: string;
    raisedByFirstName: string;
    raisedByLastName: string;
    companyName: string;
    status: SupportTicketStatus;
    severity: SupportTicketSeverity;
    canEdit: boolean;
    canClose: boolean;
}

export interface SupportTicketMessage {
    id: number;
    body: string;
    authorUsername: string;
    createdAt: string;
}

export interface SupportTicketDetail extends SupportTicketSummary {
    description: string;
    updatedAt: string;
    closedAt: string | null;
    canReply: boolean;
    messages: SupportTicketMessage[];
}

export interface SupportTicketStats {
    total: number;
    raised: number;
    inProgress: number;
    closed: number;
}

export interface SupportTicketFilters {
    ticketNumber?: string;
    title?: string;
    raisedByFirstName?: string;
    raisedByLastName?: string;
    status?: SupportTicketStatus | '';
    severity?: SupportTicketSeverity | '';
    companyName?: string;
    page?: number;
    size?: number;
}

const defaultHeaders = { 'Content-Type': 'application/json' };
const defaultOptions: RequestInit = { credentials: 'include', headers: defaultHeaders };

async function parseError(res: Response, fallback: string): Promise<string> {
    try {
        const data = await res.json();
        return data?.detail || data?.message || data?.title || fallback;
    } catch {
        return fallback;
    }
}

function toQuery(filters: SupportTicketFilters = {}): string {
    const params = new URLSearchParams();
    params.set('page', String(filters.page ?? 0));
    params.set('size', String(filters.size ?? 10));
    if (filters.ticketNumber?.trim()) params.set('ticketNumber', filters.ticketNumber.trim());
    if (filters.title?.trim()) params.set('title', filters.title.trim());
    if (filters.raisedByFirstName?.trim()) params.set('raisedByFirstName', filters.raisedByFirstName.trim());
    if (filters.raisedByLastName?.trim()) params.set('raisedByLastName', filters.raisedByLastName.trim());
    if (filters.status) params.set('status', filters.status);
    if (filters.severity) params.set('severity', filters.severity);
    if (filters.companyName?.trim()) params.set('companyName', filters.companyName.trim());
    return params.toString();
}

export function formatSupportTicketDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return iso;
    }
}

export function formatSupportTicketStatus(status: SupportTicketStatus): string {
    switch (status) {
        case 'RAISED':
            return 'Raised';
        case 'IN_PROGRESS':
            return 'In Progress';
        case 'CLOSED':
            return 'Closed';
        default:
            return status;
    }
}

export function formatSupportTicketSeverity(severity: SupportTicketSeverity): string {
    switch (severity) {
        case 'HIGH':
            return 'High';
        case 'MEDIUM':
            return 'Medium';
        case 'LOW':
            return 'Low';
        default:
            return severity;
    }
}

export async function fetchSupportTickets(
    filters: SupportTicketFilters = {},
): Promise<PageResponse<SupportTicketSummary>> {
    const res = await apiClient(buildApiUrl(`api/support-tickets?${toQuery(filters)}`), defaultOptions);
    if (!res.ok) throw new Error(await parseError(res, 'Failed to load support tickets.'));
    return res.json();
}

export async function fetchSupportTicket(id: number): Promise<SupportTicketDetail> {
    const res = await apiClient(buildApiUrl(`api/support-tickets/${id}`), defaultOptions);
    if (!res.ok) throw new Error(await parseError(res, 'Failed to load support ticket.'));
    return res.json();
}

export async function createSupportTicket(
    title: string,
    description: string,
    severity: SupportTicketSeverity,
): Promise<SupportTicketSummary> {
    const res = await apiClient(buildApiUrl('api/support-tickets'), {
        ...defaultOptions,
        method: 'POST',
        body: JSON.stringify({ title, description, severity }),
    });
    if (!res.ok) throw new Error(await parseError(res, 'Failed to submit support ticket.'));
    return res.json();
}

export async function updateSupportTicket(
    id: number,
    payload: {
        title: string;
        severity: SupportTicketSeverity;
        status: SupportTicketStatus;
    },
): Promise<SupportTicketDetail> {
    const res = await apiClient(buildApiUrl(`api/support-tickets/${id}`), {
        ...defaultOptions,
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await parseError(res, 'Failed to update support ticket.'));
    return res.json();
}

export async function closeSupportTicket(id: number): Promise<SupportTicketDetail> {
    const res = await apiClient(buildApiUrl(`api/support-tickets/${id}/close`), {
        ...defaultOptions,
        method: 'PATCH',
    });
    if (!res.ok) throw new Error(await parseError(res, 'Failed to close support ticket.'));
    return res.json();
}

export async function fetchPolarisSupportTickets(
    filters: SupportTicketFilters = {},
): Promise<PageResponse<SupportTicketSummary>> {
    const res = await apiClient(buildApiUrl(`api/polaris/support-tickets?${toQuery(filters)}`), defaultOptions);
    if (!res.ok) throw new Error(await parseError(res, 'Failed to load support tickets.'));
    return res.json();
}

export async function fetchPolarisSupportTicketStats(
    filters: Omit<SupportTicketFilters, 'page' | 'size'> = {},
): Promise<SupportTicketStats> {
    const params = new URLSearchParams();
    if (filters.ticketNumber?.trim()) params.set('ticketNumber', filters.ticketNumber.trim());
    if (filters.title?.trim()) params.set('title', filters.title.trim());
    if (filters.raisedByFirstName?.trim()) params.set('raisedByFirstName', filters.raisedByFirstName.trim());
    if (filters.raisedByLastName?.trim()) params.set('raisedByLastName', filters.raisedByLastName.trim());
    if (filters.status) params.set('status', filters.status);
    if (filters.severity) params.set('severity', filters.severity);
    if (filters.companyName?.trim()) params.set('companyName', filters.companyName.trim());
    const qs = params.toString();
    const res = await apiClient(
        buildApiUrl(`api/polaris/support-tickets/stats${qs ? `?${qs}` : ''}`),
        defaultOptions,
    );
    if (!res.ok) throw new Error(await parseError(res, 'Failed to load support ticket stats.'));
    return res.json();
}

export async function fetchPolarisSupportTicket(id: number): Promise<SupportTicketDetail> {
    const res = await apiClient(buildApiUrl(`api/polaris/support-tickets/${id}`), defaultOptions);
    if (!res.ok) throw new Error(await parseError(res, 'Failed to load support ticket.'));
    return res.json();
}

export async function updatePolarisSupportTicket(
    id: number,
    payload: {
        title: string;
        severity: SupportTicketSeverity;
        status: SupportTicketStatus;
    },
): Promise<SupportTicketDetail> {
    const res = await apiClient(buildApiUrl(`api/polaris/support-tickets/${id}`), {
        ...defaultOptions,
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await parseError(res, 'Failed to update support ticket.'));
    return res.json();
}

export async function addSupportTicketMessage(id: number, body: string): Promise<SupportTicketMessage> {
    const res = await apiClient(buildApiUrl(`api/support-tickets/${id}/messages`), {
        ...defaultOptions,
        method: 'POST',
        body: JSON.stringify({ body }),
    });
    if (!res.ok) throw new Error(await parseError(res, 'Failed to send message.'));
    return res.json();
}

export async function addPolarisSupportTicketMessage(id: number, body: string): Promise<SupportTicketMessage> {
    const res = await apiClient(buildApiUrl(`api/polaris/support-tickets/${id}/messages`), {
        ...defaultOptions,
        method: 'POST',
        body: JSON.stringify({ body }),
    });
    if (!res.ok) throw new Error(await parseError(res, 'Failed to send message.'));
    return res.json();
}
